import React, { Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AuthGuard from "components/AuthGuard";
import PageTransition from "components/ui/PageTransition";
import NotFound from "pages/NotFound";

const AiInsightsDashboard = React.lazy(() => import('./pages/ai-insights-dashboard'));
const DashboardOverview = React.lazy(() => import('./pages/dashboard-overview'));
const AnalyticsDashboard = React.lazy(() => import('./pages/analytics-dashboard'));
const DailyJournalEntry = React.lazy(() => import('./pages/daily-journal-entry'));
const LoginRegister = React.lazy(() => import('./pages/login-register'));
const ChildProfileManagement = React.lazy(() => import('./pages/child-profile-management'));
const JournalHistory = React.lazy(() => import('./pages/journal-history'));
const ConfirmationSent = React.lazy(() => import('./pages/confirmation-sent'));
const LandingPage = React.lazy(() => import('./pages/landing-page'));
const WorryVault = React.lazy(() => import('./pages/worry-vault'));
const WeeklyDigest = React.lazy(() => import('./pages/weekly-digest'));

// New Stripe payment pages
const SubscriptionCheckout = React.lazy(() => import('./pages/subscription-checkout'));
const SubscriptionSuccess = React.lazy(() => import('./pages/subscription-success'));

// Admin dashboard
const AdminDashboard = React.lazy(() => import('./pages/admin-dashboard'));

// Legal pages
const PrivacyPolicy = React.lazy(() => import('./pages/privacy-policy'));
const TermsOfService = React.lazy(() => import('./pages/terms-of-service'));

// Contact Us page
const ContactUs = React.lazy(() => import('./pages/contact-us'));

// Blog pages
const BlogList = React.lazy(() => import('./pages/blog-list'));
const BlogDetail = React.lazy(() => import('./pages/blog-detail'));

import PageSkeleton from "components/ui/PageSkeleton";

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<PageSkeleton />}>
          <RouterRoutes>
            {/* Public routes - no auth required */}
            <Route path="/" element={
              <PageTransition>
                <LandingPage />
              </PageTransition>
            } />
            <Route path="/privacy-policy" element={
              <PageTransition>
                <PrivacyPolicy />
              </PageTransition>
            } />
            <Route path="/terms-of-service" element={
              <PageTransition>
                <TermsOfService />
              </PageTransition>
            } />
            <Route path="/contact-us" element={
              <PageTransition>
                <ContactUs />
              </PageTransition>
            } />
            <Route path="/blog" element={
              <PageTransition>
                <BlogList />
              </PageTransition>
            } />
            <Route path="/blog/:slug" element={
              <PageTransition>
                <BlogDetail />
              </PageTransition>
            } />

            {/* Auth route - redirect if already authenticated */}
            <Route
              path="/login-register"
              element={
                <AuthGuard requiredAuth={false}>
                  <PageTransition>
                    <LoginRegister />
                  </PageTransition>
                </AuthGuard>
              }
            />

            <Route
              path="/confirmation-sent"
              element={
                <PageTransition>
                  <ConfirmationSent />
                </PageTransition>
              }
            />

            {/* Protected routes - require authentication */}
            <Route
              path="/dashboard"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <DashboardOverview />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/dashboard-overview"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <DashboardOverview />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/insights"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <DashboardOverview />
                  </PageTransition>
                </AuthGuard>
              }
            />

            {/* Analytics route */}
            <Route
              path="/analytics-dashboard"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <AnalyticsDashboard />
                  </PageTransition>
                </AuthGuard>
              }
            />

            {/* New AI Insights Dashboard Route */}
            <Route
              path="/ai-insights-dashboard"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <AiInsightsDashboard />
                  </PageTransition>
                </AuthGuard>
              }
            />

            <Route
              path="/profile"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <ChildProfileManagement />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/journal-entry"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <DailyJournalEntry />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/daily-journal-entry"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <DailyJournalEntry />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/worry-vault"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <WorryVault />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/weekly-digest/:id"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <WeeklyDigest />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/weekly-digest"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <WeeklyDigest />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/child-profiles"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <ChildProfileManagement />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/child-profile-management"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <ChildProfileManagement />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/journal-history"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <JournalHistory />
                  </PageTransition>
                </AuthGuard>
              }
            />

            {/* Payment routes - require authentication */}
            <Route
              path="/subscription-checkout"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <SubscriptionCheckout />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/subscription-success"
              element={
                <AuthGuard requiredAuth={true}>
                  <PageTransition>
                    <SubscriptionSuccess />
                  </PageTransition>
                </AuthGuard>
              }
            />

            {/* Admin dashboard - require authentication and admin role */}
            <Route
              path="/admin"
              element={
                <AuthGuard requiredAuth={true} requireAdmin={true}>
                  <PageTransition>
                    <AdminDashboard />
                  </PageTransition>
                </AuthGuard>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <AuthGuard requiredAuth={true} requireAdmin={true}>
                  <PageTransition>
                    <AdminDashboard />
                  </PageTransition>
                </AuthGuard>
              }
            />

            {/* Catch-all route for 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;